<script setup lang="ts">
import { ref, watch } from 'vue'
import type { ApiClient } from '@xjlv2/api-client'
import type {
  AudienceType,
  NotificationStats,
  NotificationView,
  NotificationChannelCode,
  NotificationDetail,
  PushApp
} from './types'
import { AUDIENCE_TYPES, NOTIFICATION_CHANNELS, pushAppOptions } from './types'
import { formatTime } from './usePushCenterState'
import { Send } from '@lucide/vue'
import {
  AdminDataTable,
  AdminDialog,
  AdminFilterBar,
  AdminStatusTag,
  RrButton,
  RrInput,
  RrSelect,
  RrTag,
  type TableColumnType
} from '@xjlv2/admin-layout'

const props = withDefaults(
  defineProps<{
    notifications?: NotificationView[]
    notificationStats?: NotificationStats | null
    total: number
    page: number
    pageSize: number
    keyword: string
    category: string
    queryLoading?: boolean
    apps?: PushApp[]
    appCode?: string
    canManage?: boolean
    createApiClient: () => ApiClient
    revokeNotification: (notificationId: number) => Promise<void>
    loadNotificationDetail: (notificationId: number) => Promise<NotificationDetail>
  }>(),
  {
    notifications: () => [],
    notificationStats: null,
    apps: () => [],
    appCode: '',
    canManage: false,
    queryLoading: false
  }
)

const emit = defineEmits<{
  'update:appCode': [code: string]
  refresh: []
  search: [keyword: string, category: string, appCode: string]
  'page-change': [pageNo: number, pageSize: number]
  notice: [msg: string]
  error: [msg: string]
}>()

const localAppCode = ref(props.appCode)
watch(
  () => props.appCode,
  (code) => {
    localAppCode.value = code
  }
)

function onAppChange(code: string) {
  emit('update:appCode', code)
}

const keyword = ref(props.keyword)
const categoryFilter = ref<string>(props.category || 'ALL')
watch(
  () => props.keyword,
  (value) => {
    keyword.value = value
  }
)
watch(
  () => props.category,
  (value) => {
    categoryFilter.value = value || 'ALL'
  }
)
const dialogVisible = ref(false)
const loading = ref(false)
const actingNotificationId = ref<number | null>(null)
const detailVisible = ref(false)
const detailLoading = ref(false)
const notificationDetail = ref<NotificationDetail | null>(null)

function canRevoke(notification: NotificationView) {
  return notification.status !== 'REVOKED'
}

async function openDetail(notification: NotificationView) {
  detailLoading.value = true
  detailVisible.value = true
  try {
    notificationDetail.value = await props.loadNotificationDetail(notification.id)
  } catch (err) {
    detailVisible.value = false
    emit('error', err instanceof Error ? err.message : '加载通知详情失败')
  } finally {
    detailLoading.value = false
  }
}

async function handleRevoke(notification: NotificationView) {
  if (!props.canManage || !canRevoke(notification) || actingNotificationId.value !== null) return
  if (
    !window.confirm(
      `确定撤销通知「${notification.title}」吗？未提交的外部尝试会停止，已送达内容不会被召回。`
    )
  ) {
    return
  }
  actingNotificationId.value = notification.id
  try {
    await props.revokeNotification(notification.id)
    emit('refresh')
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '通知撤销失败')
  } finally {
    actingNotificationId.value = null
  }
}

const columns: TableColumnType<NotificationView>[] = [
  { key: 'id', title: 'ID', width: 80 },
  { key: 'content', title: '标题与内容', minWidth: 220 },
  { key: 'category', title: '分类', width: 120 },
  { key: 'audience', title: '受众', width: 120 },
  { key: 'inbox', title: '站内收件/未读', width: 130 },
  { key: 'delivery', title: '通道投递结果', width: 140 },
  { key: 'status', title: '状态', width: 100 },
  { key: 'createTime', title: '发布时间', width: 180 },
  { key: 'actions', title: '操作', width: 180 }
]

const form = ref({
  appCode: props.appCode,
  moduleCode: props.appCode || 'notification',
  productCode: `${props.appCode || 'app'}-client`,
  eventCode: '',
  category: 'activity',
  title: '',
  body: '',
  route: '/pages/messages',
  audienceType: 'ALL' as AudienceType,
  userIds: '',
  tags: '',
  channels: ['INBOX', 'APP_PUSH'] as NotificationChannelCode[],
  variables: '{}',
  payload: '{}'
})

function handleSearch() {
  emit(
    'search',
    keyword.value.trim(),
    categoryFilter.value === 'ALL' ? '' : categoryFilter.value,
    localAppCode.value
  )
}

function handleReset() {
  keyword.value = ''
  categoryFilter.value = 'ALL'
  localAppCode.value = ''
  emit('update:appCode', '')
  emit('search', '', '', '')
}

function openDialog() {
  const currentApp = localAppCode.value || props.appCode
  form.value = {
    appCode: currentApp,
    moduleCode: currentApp || 'notification',
    productCode: `${currentApp || 'app'}-client`,
    eventCode: '',
    category: 'activity',
    title: '',
    body: '',
    route: '/pages/messages',
    audienceType: 'ALL',
    userIds: '',
    tags: '',
    channels: ['INBOX', 'APP_PUSH'],
    variables: '{}',
    payload: '{}'
  }
  dialogVisible.value = true
}

async function sendNotification() {
  if (!props.canManage) return
  loading.value = true
  try {
    const api = props.createApiClient()
    const payload = {
      ...form.value,
      variables: JSON.parse(form.value.variables || '{}'),
      payload: JSON.parse(form.value.payload || '{}')
    }
    await api.post('/api/saas/push/notifications', payload)
    emit('notice', `通知 "${form.value.title}" 已成功发布`)
    dialogVisible.value = false
    emit('refresh')
  } catch (err) {
    emit('error', err instanceof Error ? err.message : '通知发布失败')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="push-tab-pane">
    <AdminFilterBar :loading="loading" @submit="handleSearch" @reset="handleReset">
      <label>
        所属应用
        <RrSelect
          v-model:value="localAppCode"
          :options="pushAppOptions(apps)"
          @change="onAppChange"
        />
      </label>
      <label>
        关键字
        <RrInput
          v-model:value="keyword"
          placeholder="搜索通知标题 / 内容"
          @keyup.enter="handleSearch"
        />
      </label>
      <label>
        通知分类
        <RrSelect
          v-model:value="categoryFilter"
          :options="[
            { label: '全部分类', value: 'ALL' },
            { label: 'activity (活动)', value: 'activity' },
            { label: 'assistant (助手)', value: 'assistant' },
            { label: 'wallet (钱包)', value: 'wallet' },
            { label: 'order (订单)', value: 'order' },
            { label: 'system (系统)', value: 'system' }
          ]"
          @change="handleSearch"
        />
      </label>
      <template #actions>
        <RrButton type="primary" :disabled="!canManage" @click="openDialog()">
          <template #icon><Send :size="16" /></template>
          发布新通知
        </RrButton>
      </template>
    </AdminFilterBar>

    <AdminDataTable
      :columns="columns"
      :data-source="notifications"
      :loading="queryLoading"
      :current="page"
      :page-size="pageSize"
      :total="total"
      :empty="notifications.length === 0"
      empty-title="暂无通知记录"
      empty-description="可点击上方「发布新通知」按钮发送消息。"
      @update:current="(p) => emit('page-change', p, pageSize)"
      @update:page-size="(size) => emit('page-change', 1, size)"
      @refresh="handleSearch"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'id'">
          <code>#{{ record.id }}</code>
        </template>
        <template v-else-if="column.key === 'content'">
          <div class="template-content-cell">
            <span class="tpl-title">{{ record.title }}</span>
            <small class="tpl-body">{{ record.body }}</small>
          </div>
        </template>
        <template v-else-if="column.key === 'category'">
          <RrTag tone="neutral" :label="record.category" />
        </template>
        <template v-else-if="column.key === 'audience'">
          <RrTag tone="primary" :label="record.audienceType" />
        </template>
        <template v-else-if="column.key === 'inbox'">
          {{ record.inboxCount }} / {{ record.unreadCount }}
        </template>
        <template v-else-if="column.key === 'delivery'">
          <span class="text-green">{{ record.deliveredCount }} 成功</span> /
          <span class="text-red">{{ record.failedCount }} 失败</span>
        </template>
        <template v-else-if="column.key === 'status'">
          <AdminStatusTag
            :text="record.status"
            :tone="record.status === 'SENT' ? 'success' : 'neutral'"
          />
        </template>
        <template v-else-if="column.key === 'createTime'">
          <span class="text-time">{{ formatTime(record.createTime) }}</span>
        </template>
        <template v-else-if="column.key === 'actions'">
          <div class="row-actions">
            <RrButton html-type="button" size="small" @click="openDetail(record)">详情</RrButton>
            <RrButton
              v-if="canManage && canRevoke(record)"
              html-type="button"
              size="small"
              danger
              :loading="actingNotificationId === record.id"
              @click="handleRevoke(record)"
            >
              撤销
            </RrButton>
          </div>
        </template>
      </template>
    </AdminDataTable>

    <!-- 发布通知标准弹窗 -->
    <AdminDialog
      v-model:open="dialogVisible"
      title="发布新通知"
      :width="640"
      :confirm-loading="loading"
      ok-text="立即发布"
      @ok="sendNotification"
      @cancel="dialogVisible = false"
    >
      <div class="modal-form-grid">
        <div class="form-row-2">
          <label class="form-field">
            <span class="field-label required">应用编码</span>
            <RrInput
              v-model:value.trim="form.appCode"
              placeholder="请输入应用编码"
              autocomplete="off"
            />
          </label>
          <label class="form-field">
            <span class="field-label required">通知分类</span>
            <RrSelect
              v-model:value="form.category"
              :options="[
                { label: 'activity (活动)', value: 'activity' },
                { label: 'assistant (助手)', value: 'assistant' },
                { label: 'wallet (钱包)', value: 'wallet' },
                { label: 'order (订单)', value: 'order' },
                { label: 'system (系统)', value: 'system' }
              ]"
            />
          </label>
        </div>

        <div class="form-row-2">
          <label class="form-field">
            <span class="field-label required">受众类型</span>
            <RrSelect
              v-model:value="form.audienceType"
              :options="AUDIENCE_TYPES.map((type) => ({ label: type, value: type }))"
            />
          </label>
          <label class="form-field">
            <span class="field-label">用户 IDs (可选)</span>
            <RrInput
              v-model:value.trim="form.userIds"
              placeholder="多个用英文逗号分隔"
              autocomplete="off"
            />
          </label>
        </div>

        <label class="form-field">
          <span class="field-label required">通知标题</span>
          <RrInput
            v-model:value.trim="form.title"
            placeholder="请输入通知标题"
            autocomplete="off"
          />
        </label>

        <label class="form-field">
          <span class="field-label required">通知内容</span>
          <RrInput
            v-model:value="form.body"
            type="textarea"
            :rows="3"
            placeholder="请输入通知详细文本..."
          />
        </label>

        <label class="form-field">
          <span class="field-label">客户端跳转路由</span>
          <RrInput
            v-model:value.trim="form.route"
            placeholder="/pages/messages"
            autocomplete="off"
          />
        </label>

        <div class="form-section-title">投递渠道</div>
        <div class="channel-options-grid">
          <label v-for="ch in NOTIFICATION_CHANNELS" :key="ch.code" class="channel-check-card">
            <input type="checkbox" v-model="form.channels" :value="ch.code" />
            <div>
              <strong>{{ ch.label }}</strong>
              <small>{{ ch.detail }}</small>
            </div>
          </label>
        </div>
      </div>
    </AdminDialog>

    <AdminDialog
      v-model:open="detailVisible"
      title="通知详情"
      :width="720"
      :show-cancel="false"
      ok-text="关闭"
      :confirm-loading="detailLoading"
      @ok="detailVisible = false"
      @cancel="detailVisible = false"
    >
      <div v-if="notificationDetail" class="detail-stack">
        <p>
          <strong>{{ notificationDetail.notification.title }}</strong>
          · {{ notificationDetail.notification.status }} ·
          {{ notificationDetail.notification.appCode }}
        </p>
        <p class="text-time">
          收件 {{ (notificationDetail.inboxRecords || []).length }} · 任务
          {{ (notificationDetail.pushTasks || []).length }} · 投递
          {{ (notificationDetail.deliveries || []).length }}
        </p>
        <ul class="detail-list">
          <li v-for="task in (notificationDetail.pushTasks || []).slice(0, 10)" :key="task.id">
            任务 #{{ task.id }} {{ task.provider }} {{ task.status }}
          </li>
          <li
            v-for="delivery in (notificationDetail.deliveries || []).slice(0, 10)"
            :key="delivery.id"
          >
            投递 #{{ delivery.id }} {{ delivery.provider }} {{ delivery.status }}
          </li>
        </ul>
      </div>
      <p v-else-if="detailLoading" class="text-time">正在加载通知详情…</p>
    </AdminDialog>
  </div>
</template>

<style scoped>
.push-tab-pane {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4);
}

.template-content-cell {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-1);
  max-width: 480px;
}

.tpl-title {
  font-weight: var(--rr-font-weight-bold);
  color: var(--rr-color-text-strong);
  font-size: var(--rr-font-size-base);
}

.tpl-body {
  color: var(--rr-color-text-muted);
  font-size: var(--rr-font-size-sm);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: var(--rr-line-height-base);
}

.text-time {
  font-size: var(--rr-font-size-sm);
  color: var(--rr-color-text-muted);
}

.text-green {
  color: var(--rr-color-success);
  font-weight: var(--rr-font-weight-bold);
}
.text-red {
  color: var(--rr-color-danger);
  font-weight: var(--rr-font-weight-bold);
}

.modal-form-grid {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-4);
  padding: var(--rr-space-2) 0;
}

.form-section-title {
  font-size: var(--rr-font-size-base);
  font-weight: var(--rr-font-weight-bold);
  color: var(--rr-color-text-strong);
  border-bottom: 1px solid var(--rr-color-border);
  padding-bottom: var(--rr-space-2);
  margin-top: var(--rr-space-1);
}

.form-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--rr-space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-2);
}

.field-label {
  font-size: var(--rr-font-size-base);
  font-weight: var(--rr-font-weight-bold);
  color: var(--rr-color-text);
}

.field-label.required::after {
  content: ' *';
  color: var(--rr-color-danger);
}

.channel-options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--rr-space-3);
}

.channel-check-card {
  display: flex;
  align-items: flex-start;
  gap: var(--rr-space-2);
  padding: var(--rr-space-3);
  border: 1px solid var(--rr-color-border);
  border-radius: var(--rr-radius-control);
  cursor: pointer;
  background: var(--rr-color-surface);
}

.channel-check-card strong {
  display: block;
  font-size: var(--rr-font-size-base);
  color: var(--rr-color-text-strong);
}

.channel-check-card small {
  display: block;
  font-size: var(--rr-font-size-xs);
  color: var(--rr-color-text-muted);
}

.row-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--rr-space-2);
}

.detail-stack {
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-2);
}

.detail-list {
  margin: 0;
  padding-left: var(--rr-space-5);
  display: flex;
  flex-direction: column;
  gap: var(--rr-space-1);
  color: var(--rr-color-text);
  font-size: var(--rr-font-size-base);
}
</style>
